/**
 * PhotoCarousel Component
 * 
 * Full-screen photo carousel with dish details overlay
 */
import React, { useState } from 'react';

function PhotoCarousel({ review, initialIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showDishDetails, setShowDishDetails] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const currentPhoto = review.photos[currentIndex];
  const currentDish = review.dishes?.find(d => d.photo_index === currentIndex);

  const handlePrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : review.photos.length - 1));
    setShowDishDetails(false);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < review.photos.length - 1 ? prev + 1 : 0));
    setShowDishDetails(false);
  };

  const toggleDishDetails = (e) => {
    // Only toggle if clicking on the image itself, not buttons or arrows
    if (e.target.tagName === 'IMG' || e.target.classList.contains('image-container')) {
      if (currentDish) {
        setShowDishDetails(!showDishDetails);
      }
    }
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    setShowDishDetails(false); // Hide dish details when expanding
  };

  const handleBackdropClick = (e) => {
    // Close modal if clicking on backdrop (not on image or controls)
    if (e.target.classList.contains('modal-backdrop')) {
      if (isExpanded) {
        setIsExpanded(false);
      } else {
        onClose();
      }
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center modal-backdrop" 
      onClick={handleBackdropClick}
      data-testid="photo-carousel-modal"
    >
      {/* Close Button - Top Right */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white text-3xl hover:text-gray-300 transition-colors z-30 w-10 h-10 flex items-center justify-center"
        data-testid="close-carousel-button"
      >
        ✕
      </button>

      {/* Restaurant Name - Top Left */}
      <div className="absolute top-6 left-6 text-white z-30">
        <p className="font-semibold text-lg">{review.restaurant_name}</p>
      </div>

      {/* Image Container - Max 50% of screen (or full screen when expanded), centered */}
      <div 
        className={`relative flex items-center justify-center transition-all duration-300 ${
          isExpanded ? 'w-full h-full' : ''
        }`}
        style={isExpanded ? {} : { maxWidth: '50vw', maxHeight: '50vh' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image */}
        <div 
          className="relative image-container cursor-pointer"
          onClick={toggleDishDetails}
        >
          <img
            src={currentPhoto}
            alt={review.restaurant_name}
            className={`object-contain transition-all duration-300 ${
              isExpanded ? 'max-w-[90vw] max-h-[90vh]' : 'max-w-full max-h-full rounded-lg'
            } ${showDishDetails && currentDish ? 'opacity-90' : 'opacity-100'}`}
            style={isExpanded ? {} : { maxWidth: '50vw', maxHeight: '50vh' }}
            data-testid={`carousel-image-${currentIndex}`}
          />
          
          {/* Image Counter - Top Right of image */}
          {review.photos.length > 1 && isHovered && !isExpanded && (
            <div 
              className="absolute top-3 right-3 bg-black/80 text-white px-3 py-1.5 rounded-lg text-sm font-semibold z-20"
              data-testid="image-counter"
            >
              {currentIndex + 1} / {review.photos.length}
            </div>
          )}
          
          {/* Expand/Collapse Button */}
          {isHovered && (
            <button
              onClick={handleExpandClick}
              className={`absolute bg-black/80 text-white w-10 h-10 rounded-lg flex items-center justify-center hover:bg-black/90 transition-all z-20 ${
                isExpanded ? 'top-3 right-3' : 'bottom-3 right-3'
              }`}
              data-testid={isExpanded ? "collapse-button" : "expand-button"}
              title={isExpanded ? "Collapse image" : "Expand image"}
            >
              {isExpanded ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9l6 6m0-6l-6 6m12-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
          )}
          
          {/* Dish Details Overlay - Centered on image */}
          {showDishDetails && currentDish && !isExpanded && (
            <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none animate-fade-in" data-testid="dish-details-overlay">
              <div className="text-center max-w-md bg-black/50 p-6 rounded-xl backdrop-blur-md">
                <h4 className="text-white font-bold text-2xl mb-3 drop-shadow-lg">{currentDish.dish_name}</h4>
                <p className="text-white text-base leading-relaxed drop-shadow-lg">{currentDish.notes}</p>
              </div>
            </div>
          )}
          
          {/* Tap Hint - Only when not expanded */}
          {currentDish && !showDishDetails && isHovered && !isExpanded && (
            <div className="absolute bottom-14 left-1/2 transform -translate-x-1/2 bg-black/80 text-white px-4 py-2 rounded-full text-xs">
              Click to see dish details
            </div>
          )}
        </div>
        
        {/* Navigation Arrows - Outside image, on sides (or on image when expanded) */}
        {review.photos.length > 1 && isHovered && (
          <>
            <button
              onClick={handlePrevious}
              className={`absolute top-1/2 transform -translate-y-1/2 bg-white/20 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-white/30 transition-all text-2xl z-20 backdrop-blur-sm ${
                isExpanded ? 'left-4' : '-left-20'
              }`}
              data-testid="carousel-prev-button"
            >
              ‹
            </button>
            <button
              onClick={handleNext}
              className={`absolute top-1/2 transform -translate-y-1/2 bg-white/20 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-white/30 transition-all text-2xl z-20 backdrop-blur-sm ${
                isExpanded ? 'right-4' : '-right-20'
              }`}
              data-testid="carousel-next-button"
            >
              ›
            </button>
          </>
        )}
      </div>

      {/* Dots Indicator - Below image (only when not expanded) */}
      {review.photos.length > 1 && !isExpanded && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
          {review.photos.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'bg-white w-6' : 'bg-white/40'
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      )}
      
      {/* Image Counter when expanded - Top Right */}
      {review.photos.length > 1 && isExpanded && (
        <div className="absolute top-8 right-20 bg-black/80 text-white px-4 py-2 rounded-lg text-sm font-semibold z-10">
          {currentIndex + 1} / {review.photos.length}
        </div>
      )}
    </div>
  );
}

export default PhotoCarousel;
